import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { FaTrash, FaMobileAlt, FaSignOutAlt, FaSpinner, FaCheck, FaTimes, FaUserMinus, FaUserAltSlash } from 'react-icons/fa';
import './user_board.css';

const UserBoard = () => {
  // # State for tracking function calls and confirmation dialogs
  const [loading, setLoading] = useState({
    auth: false,
    devices: false,
    signout: false,
    unverified: false,
    incomplete: false
  });
  const [results, setResults] = useState({
    auth: null,
    devices: null,
    signout: null,
    unverified: null,
    incomplete: null
  });
  const [showConfirm, setShowConfirm] = useState({
    auth: false,
    devices: false,
    signout: false,
    unverified: false,
    incomplete: false
  });

  // # Initialize Firebase functions with the correct region
  const functions = getFunctions(undefined, 'europe-west1');
  const manualDeleteOldAuthData = httpsCallable(functions, 'manualDeleteOldAuthData');
  const cleanAllDevices = httpsCallable(functions, 'cleanAllDevices');
  const manualSignOutAllUsers = httpsCallable(functions, 'manualSignOutAllUsers');
  const manualDeleteOldUnverifiedUsers = httpsCallable(functions, 'manualDeleteOldUnverifiedUsers');
  const manualDeleteIncompleteSignups = httpsCallable(functions, 'manualDeleteIncompleteSignups');

  // # Handle confirmation dialog
  const handleConfirmOpen = (type) => {
    setShowConfirm(prev => ({ ...prev, [type]: true }));
  };

  const handleConfirmClose = (type) => {
    setShowConfirm(prev => ({ ...prev, [type]: false }));
  };

  // # Handle cleanup function execution
  const handleCleanup = async (type) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    setResults(prev => ({ ...prev, [type]: null }));
    setShowConfirm(prev => ({ ...prev, [type]: false }));
    
    try {
      let result;
      
      // # Call the appropriate function based on type
      switch (type) {
        case 'auth':
          result = await manualDeleteOldAuthData();
          break;
        case 'devices':
          result = await cleanAllDevices();
          break;
        case 'signout':
          result = await manualSignOutAllUsers();
          break;
        case 'unverified':
          // # For demonstration in emulator, use a short cutoff time (1 minute)
          const isProdUnverified = process.env.NODE_ENV === 'production';
          const cutoffHoursUnverified = isProdUnverified ? 72 : 1/60; // 72 hours in production, 1 minute in development
          result = await manualDeleteOldUnverifiedUsers({ cutoffHours: cutoffHoursUnverified, dryRun: false });
          break;
        case 'incomplete':
          // # For demonstration in emulator, use a short cutoff time (1 minute)
          const isProdIncomplete = process.env.NODE_ENV === 'production';
          const cutoffMinutesIncomplete = isProdIncomplete ? 60 : 1; // 60 minutes in production, 1 minute in development
          result = await manualDeleteIncompleteSignups({ cutoffMinutes: cutoffMinutesIncomplete, dryRun: false });
          break;
        default:
          throw new Error('Unknown cleanup type');
      }
      
      // # Update results state
      setResults(prev => ({ ...prev, [type]: { 
        success: result.data.success,
        message: result.data.message,
        timestamp: result.data.timestamp
      }}));
    } catch (error) {
      console.error(`Error executing ${type} cleanup:`, error);
      setResults(prev => ({ ...prev, [type]: { 
        success: false,
        message: error.message || 'An error occurred',
        timestamp: new Date().toISOString()
      }}));
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // # Render result status
  const renderResult = (type) => {
    const result = results[type];
    
    if (!result) return null;
    
    return (
      <div className={`tech_usr_result ${result.success ? 'tech_usr_success' : 'tech_usr_error'}`}>
        <div className="tech_usr_result-icon">
          {result.success ? <FaCheck /> : <FaTimes />}
        </div>
        <div className="tech_usr_result-content">
          <p className="tech_usr_result-message">{result.message}</p>
          <p className="tech_usr_result-timestamp">
            {new Date(result.timestamp).toLocaleString()}
          </p>
        </div>
      </div>
    );
  };

  // # Render confirmation dialog
  const renderConfirmDialog = (type, title, description) => {
    if (!showConfirm[type]) return null;
    
    return (
      <div className="tech_usr_confirm-overlay">
        <div className="tech_usr_confirm-dialog">
          <h3>{title}</h3>
          <p>{description}</p>
          <div className="tech_usr_confirm-actions">
            <button 
              className="tech_usr_confirm-cancel" 
              onClick={() => handleConfirmClose(type)}
            >
              Cancel
            </button>
            <button 
              className="tech_usr_confirm-proceed" 
              onClick={() => handleCleanup(type)}
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="tech_usr_container">
      <SharedHeader />
      
      <main className="tech_usr_content">
        <div className="tech_usr_header">
          <h1>User Data Management</h1>
          <p>Manage user authentication data and cleanup operations</p>
        </div>

        <div className="tech_usr_cards">
          {/* Auth Data Cleanup Card */}
          <div className="tech_usr_card">
            <div className="tech_usr_card-header">
              <FaTrash className="tech_usr_card-icon" />
              <h2>Auth Data Cleanup</h2>
            </div>
            <div className="tech_usr_card-body">
              <p>
                Removes old authentication data (sign-ins and sign-outs) that are older than 95 days.
                This helps keep the database clean and efficient.
              </p>
              <p className="tech_usr_card-note">
                Normally runs at 3:00 AM on Sundays and Wednesdays (Cairo time).
              </p>
              {renderResult('auth')}
            </div>
            <div className="tech_usr_card-footer">
              <button 
                className="tech_usr_button"
                onClick={() => handleConfirmOpen('auth')}
                disabled={loading.auth}
              >
                {loading.auth ? <FaSpinner className="tech_usr_spinner" /> : 'Run Cleanup Now'}
              </button>
            </div>
          </div>

          {/* Device Cleanup Card */}
          <div className="tech_usr_card">
            <div className="tech_usr_card-header">
              <FaMobileAlt className="tech_usr_card-icon" />
              <h2>Device Cleanup</h2>
            </div>
            <div className="tech_usr_card-body">
              <p>
                Removes all registered devices from user accounts. This is useful for troubleshooting
                or when you need to force users to re-authenticate on all their devices.
              </p>
              <p className="tech_usr_card-note">
                This is a manual operation and should be used with caution.
              </p>
              {renderResult('devices')}
            </div>
            <div className="tech_usr_card-footer">
              <button 
                className="tech_usr_button"
                onClick={() => handleConfirmOpen('devices')}
                disabled={loading.devices}
              >
                {loading.devices ? <FaSpinner className="tech_usr_spinner" /> : 'Clear All Devices'}
              </button>
            </div>
          </div>

          {/* Sign Out All Users Card */}
          <div className="tech_usr_card">
            <div className="tech_usr_card-header">
              <FaSignOutAlt className="tech_usr_card-icon" />
              <h2>Sign Out All Users</h2>
            </div>
            <div className="tech_usr_card-body">
              <p>
                Signs out all users from all their devices by revoking Firebase Auth sessions, creating sign-out records, and clearing
                device registrations. Forces all users to re-authenticate on their next request.
              </p>
              <p className="tech_usr_card-note">
                Automatically runs every Friday at 5:00 AM (Cairo time).
              </p>
              {renderResult('signout')}
            </div>
            <div className="tech_usr_card-footer">
              <button 
                className="tech_usr_button"
                onClick={() => handleConfirmOpen('signout')}
                disabled={loading.signout}
              >
                {loading.signout ? <FaSpinner className="tech_usr_spinner" /> : 'Sign Out All Users'}
              </button>
            </div>
          </div>
          
          {/* Delete Unverified Users Card */}
          <div className="tech_usr_card">
            <div className="tech_usr_card-header">
              <FaUserMinus className="tech_usr_card-icon" />
              <h2>Delete Unverified Users</h2>
            </div>
            <div className="tech_usr_card-body">
              <p>
                Removes all user accounts that haven't verified their email within the specified time period.
                This helps keep the user database clean by removing abandoned registrations.
              </p>
              <p className="tech_usr_card-note">
                Automatically runs every Thursday at 1:00 AM (Cairo time).
              </p>
              {renderResult('unverified')}
            </div>
            <div className="tech_usr_card-footer">
              <button 
                className="tech_usr_button"
                onClick={() => handleConfirmOpen('unverified')}
                disabled={loading.unverified}
              >
                {loading.unverified ? <FaSpinner className="tech_usr_spinner" /> : 'Delete Unverified Users'}
              </button>
            </div>
          </div>

          {/* Delete Incomplete Signups Card */}
          <div className="tech_usr_card">
            <div className="tech_usr_card-header">
              <FaUserAltSlash className="tech_usr_card-icon" />
              <h2>Delete Incomplete Signups</h2>
            </div>
            <div className="tech_usr_card-body">
              <p>
                Removes all user accounts with incomplete signup process that were started more than 60 minutes ago.
                Helps maintain a clean user database by removing abandoned registration attempts.
              </p>
              <p className="tech_usr_card-note">
                Automatically runs daily at 5:00 PM (Cairo time).
              </p>
              {renderResult('incomplete')}
            </div>
            <div className="tech_usr_card-footer">
              <button 
                className="tech_usr_button"
                onClick={() => handleConfirmOpen('incomplete')}
                disabled={loading.incomplete}
              >
                {loading.incomplete ? <FaSpinner className="tech_usr_spinner" /> : 'Delete Incomplete Signups'}
              </button>
            </div>
          </div>
        </div>
      </main>

      <DashboardFooter />

      {/* Confirmation Dialogs */}
      {renderConfirmDialog(
        'auth',
        'Confirm Auth Data Cleanup',
        'This will remove old authentication data (sign-ins and sign-outs) that are older than 95 days. This operation cannot be undone. Are you sure you want to proceed?'
      )}
      
      {renderConfirmDialog(
        'devices',
        'Confirm Device Cleanup',
        'This will remove ALL registered devices from ALL user accounts. Users will need to log in again on their devices. This operation cannot be undone and may disrupt user experience. Are you sure you want to proceed?'
      )}
      
      {renderConfirmDialog(
        'signout',
        'Confirm Sign Out All Users',
        'This will sign out ALL users from ALL their devices by revoking their Firebase Auth sessions. Everyone will need to log in again. This operation cannot be undone and will immediately terminate all user sessions. Are you sure you want to proceed?'
      )}
      
      {renderConfirmDialog(
        'unverified',
        'Confirm Delete Unverified Users',
        'This will permanently delete all user accounts that have not verified their email address within the specified time period. This includes removing all their data from Firestore. This operation cannot be undone. Are you sure you want to proceed?'
      )}

      {renderConfirmDialog(
        'incomplete',
        'Confirm Delete Incomplete Signups',
        'This will permanently delete all user accounts with incomplete signup process that were started more than 60 minutes ago (1 minute in development). This includes all their data and authentication credentials. This operation cannot be undone. Are you sure you want to proceed?'
      )}
    </div>
  );
};

export default UserBoard;
